<template>
  <main class="auth-page">
    <div class="auth-page__ambient" aria-hidden="true"></div>
    <div class="auth-page__background" aria-hidden="true">
      <div class="auth-page__grid"></div>
    </div>
    <div class="auth-page__vignette" aria-hidden="true"></div>

    <section class="auth-shell" aria-label="Trukkin OMS sign in">
      <header class="auth-topbar">
        <div class="auth-brand">
          <img
            src="../../assets/Trukkin_logo.png"
            class="auth-brand__logo"
            alt="Trukkin"
          />
          <span class="auth-brand__divider" aria-hidden="true"></span>
          <span class="auth-brand__label">Mission Control</span>
        </div>

        <div class="auth-status" aria-label="System status">
          <span class="auth-status__dot" aria-hidden="true"></span>
          <span>All systems online</span>
        </div>
      </header>

      <div class="auth-main">
        <article class="auth-hero">
          <p class="auth-hero__eyebrow">
            <v-icon aria-hidden="true">gps_fixed</v-icon>
            Trukkin&nbsp;One
          </p>

          <h1>
            Sign in to your
            <span>logistics command center</span>
          </h1>

          <p class="auth-hero__copy">
            Coordinate jobs, procurement, assignments, and account operations
            from one secure, AI-assisted workspace.
          </p>

          <div class="auth-hero__signals" aria-label="Workspace safeguards">
            <span>
              <v-icon aria-hidden="true">security</v-icon>
              Google Workspace SSO
            </span>
            <span>
              <v-icon aria-hidden="true">vpn_key</v-icon>
              Role-based access
            </span>
            <span>
              <v-icon aria-hidden="true">public</v-icon>
              Multi-country operations
            </span>
          </div>
        </article>

        <aside class="auth-access" aria-label="Secure access portal">
          <div class="auth-access__radar" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="auth-access__card">
            <div class="auth-access__accent" aria-hidden="true"></div>
            <header class="auth-access__header">
              <span class="auth-access__icon" aria-hidden="true">
                <v-icon>local_shipping</v-icon>
              </span>
              <div>
                <h2>Trukkin Mission Control</h2>
                <p>Secure access portal</p>
              </div>
            </header>

            <p class="auth-access__copy">
              Authenticate with your authorized Trukkin workspace to enter the
              console.
            </p>

            <auth-login />
          </div>
        </aside>
      </div>

      <footer class="auth-footer">
        <span>ONE.TRUKKIN.AI / UNIFIED OPERATIONS PLATFORM</span>
        <span>
          <v-icon aria-hidden="true">shield</v-icon>
          256-BIT ENCRYPTED / v2.0
        </span>
      </footer>
    </section>
  </main>
</template>

<script>
import AuthLogin from "@/components/Auth/AuthLogin";
export default {
  created() {
    this.$eventBus.$emit("show-nav-items", false);
  },
  components: {
    AuthLogin,
  },
  data() {
    return {
      active: "tab-1",
    };
  },
  methods: {
    next(active) {
      this.active = active;
    },
  },
  destroyed() {
    this.$eventBus.$emit("show-nav-items", true);
  },
};
</script>

<style scoped>
.auth-page {
  --auth-blue: #1f45c7;
  --auth-blue-soft: #6e9bff;
  --auth-green: #34d399;
  --auth-orange: #ea7a1c;
  --auth-orange-dark: #d96a10;
  --auth-ink: #f4f7ff;
  --auth-muted: #9aa7c6;
  --auth-muted-strong: #c7d0e6;
  --auth-panel: rgba(20, 27, 46, 0.8);
  --auth-panel-strong: rgba(20, 27, 46, 0.86);
  --auth-stroke: rgba(255, 255, 255, 0.12);
  background: #05070e;
  color: var(--auth-ink);
  font-family: "Geist", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  min-height: 100vh;
  min-height: 100svh;
  overflow-x: hidden;
  position: relative;
}

.auth-page__ambient,
.auth-page__background,
.auth-page__grid,
.auth-page__vignette {
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.auth-page__ambient {
  background:
    radial-gradient(
      68.75rem 43.75rem at 12% 8%,
      rgba(31, 69, 199, 0.34),
      transparent 60%
    ),
    radial-gradient(
      56.25rem 38.75rem at 92% 96%,
      rgba(234, 122, 28, 0.2),
      transparent 60%
    ),
    radial-gradient(
      43.75rem 43.75rem at 78% 12%,
      rgba(23, 51, 153, 0.28),
      transparent 62%
    );
  z-index: 0;
}

.auth-page__background {
  z-index: 0;
}

.auth-page__grid {
  --grid-cell-size: clamp(56px, 4vw, 64px);
  background-image:
    linear-gradient(rgba(120, 150, 230, 0.09) 1px, transparent 1px),
    linear-gradient(
      90deg,
      rgba(120, 150, 230, 0.09) 1px,
      transparent 1px
    );
  background-position:
    0 0,
    0 0;
  background-size: var(--grid-cell-size) var(--grid-cell-size);
  mask-image: radial-gradient(80% 80% at 50% 40%, #000 30%, transparent 90%);
  -webkit-mask-image: radial-gradient(80% 80% at 50% 40%, #000 30%, transparent 90%);
  animation: auth-grid-drift 6s linear infinite;
  will-change: background-position;
}

.auth-page__vignette {
  background: radial-gradient(
    120% 120% at 50% 50%,
    transparent 55%,
    rgba(3, 5, 12, 0.85)
  );
  z-index: 0;
}

.auth-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100svh;
  padding: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}

.auth-topbar {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  max-width: none;
  min-height: 2.75rem;
  padding: 1.625rem 2.5rem;
  position: relative;
  width: 100%;
  z-index: 2;
}

.auth-brand {
  align-items: center;
  display: flex;
  gap: 0.8125rem;
  min-width: 0;
}

.auth-brand__logo {
  display: block;
  height: 1.625rem;
  object-fit: contain;
  width: auto;
}

.auth-brand__divider {
  background: rgba(255, 255, 255, 0.16);
  flex: 0 0 auto;
  height: 1.375rem;
  width: 0.0625rem;
}

.auth-brand__label,
.auth-status,
.auth-hero__eyebrow,
.auth-access__header p,
.auth-footer {
  font-family: "Geist Mono", ui-monospace, SFMono-Regular, Consolas, monospace;
}

.auth-brand__label {
  color: #8fa0c8;
  flex: 0 0 auto;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  line-height: 1.2;
  text-transform: uppercase;
}

.auth-status {
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
  border-radius: 999rem;
  color: #b7c4e2;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.6875rem;
  font-weight: 700;
  gap: 0.5rem;
  letter-spacing: 0.12em;
  line-height: 1.2;
  padding: 0.4375rem 0.8125rem;
  text-transform: uppercase;
}

.auth-status__dot {
  animation: auth-online 2.4s infinite;
  background: var(--auth-green);
  border-radius: 999rem;
  height: 0.5rem;
  width: 0.5rem;
}

.auth-main {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 auto;
  gap: 3.5rem;
  justify-content: center;
  margin: 0 auto;
  max-width: 80rem;
  min-height: calc(100vh - 11.875rem);
  padding: 1.25rem 2.5rem 3.75rem;
  width: 100%;
}

.auth-hero {
  display: flex;
  flex-direction: column;
  flex: 1 1 26.25rem;
  max-width: 35rem;
  min-width: 20rem;
}

.auth-hero__eyebrow {
  align-items: center;
  align-self: flex-start;
  background: rgba(234, 122, 28, 0.1);
  border: 0.0625rem solid rgba(234, 122, 28, 0.26);
  border-radius: 999rem;
  color: #f2a24a;
  display: inline-flex;
  font-size: 0.6875rem;
  font-weight: 800;
  gap: 0.5rem;
  letter-spacing: 0.16em;
  line-height: 1.2;
  margin: 0 0 1.375rem;
  padding: 0.4375rem 0.75rem;
  text-transform: uppercase;
}

.auth-hero__eyebrow .v-icon {
  color: #f2a24a;
  font-size: 0.9375rem;
}

.auth-hero h1 {
  color: var(--auth-ink);
  font-size: clamp(2.35rem, 4vw, 2.875rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.06;
  margin: 0;
}

.auth-hero h1 span {
  background: linear-gradient(100deg, var(--auth-blue-soft), var(--auth-orange));
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  display: inline;
  white-space: nowrap;
}

.auth-hero__copy {
  color: var(--auth-muted);
  font-size: clamp(0.9375rem, 1.5vw, 1rem);
  font-weight: 600;
  line-height: 1.65;
  margin: 1.125rem 0 1.875rem;
  max-width: 29rem;
}

.auth-hero__signals {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.auth-hero__signals span {
  align-items: center;
  background: rgba(255, 255, 255, 0.045);
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: var(--auth-muted-strong);
  display: inline-flex;
  font-size: 0.84375rem;
  font-weight: 700;
  gap: 0.5625rem;
  line-height: 1.4;
  padding: 0.625rem 0.875rem;
}

.auth-hero__signals .v-icon {
  color: var(--auth-blue-soft);
  font-size: 1rem;
}

.auth-access {
  flex: 0 1 27.5rem;
  justify-self: end;
  max-width: 27.5rem;
  min-width: 0;
  position: relative;
  width: 100%;
}

.auth-access__radar {
  display: grid;
  inset: -4rem;
  place-items: center;
  pointer-events: none;
  position: absolute;
}

.auth-access__radar::before,
.auth-access__radar::after,
.auth-access__radar span {
  border-radius: 999rem;
  content: "";
  grid-area: 1 / 1;
  position: relative;
}

.auth-access__radar::before {
  animation: auth-radar-sweep 5s linear infinite;
  background: conic-gradient(from 0deg, rgba(110, 155, 255, 0.3), transparent 32%);
  height: 22.5rem;
  width: 22.5rem;
}

.auth-access__radar::after {
  animation: auth-radar-ring 3.4s ease-out infinite;
  border: 0.0625rem solid rgba(52, 211, 153, 0.45);
  height: 22.5rem;
  width: 22.5rem;
}

.auth-access__radar span:nth-child(1) {
  border: 0.0625rem solid rgba(110, 155, 255, 0.16);
  height: 22.5rem;
  width: 22.5rem;
}

.auth-access__radar span:nth-child(2) {
  border: 0.0625rem solid rgba(110, 155, 255, 0.12);
  height: 15.625rem;
  width: 15.625rem;
}

.auth-access__radar span:nth-child(3) {
  background: rgba(234, 122, 28, 0.78);
  box-shadow: 0 0 2rem rgba(234, 122, 28, 0.34);
  height: 0.6875rem;
  width: 0.6875rem;
}

.auth-access__card {
  animation: auth-card-float 7s ease-in-out infinite;
  background:
    linear-gradient(180deg, var(--auth-panel-strong), var(--auth-panel)),
    linear-gradient(135deg, rgba(110, 155, 255, 0.08), transparent 38%);
  border: 0.0625rem solid var(--auth-stroke);
  border-radius: 1.375rem;
  box-shadow:
    0 2.5rem 5.625rem -1.875rem rgba(0, 0, 0, 0.8),
    inset 0 0.0625rem 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  overflow: hidden;
  padding: clamp(1.25rem, 3vw, 2rem);
  position: relative;
  z-index: 1;
}

.auth-access__accent {
  background: linear-gradient(90deg, var(--auth-blue-soft), var(--auth-orange));
  border-radius: 999rem;
  box-shadow: 0 0 1.25rem rgba(110, 155, 255, 0.46);
  height: 0.1875rem;
  margin: -0.25rem -0.25rem 1.5rem;
}

.auth-access__header {
  align-items: center;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: auto minmax(0, 1fr);
  margin-bottom: 0.875rem;
}

.auth-access__icon {
  align-items: center;
  background: linear-gradient(160deg, var(--auth-blue), #122878);
  border-radius: 0.5rem;
  box-shadow: 0 0.75rem 1.5rem rgba(31, 69, 199, 0.32);
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  width: 2.5rem;
}

.auth-access__icon .v-icon {
  color: var(--auth-orange);
  font-size: 1.3125rem;
}

.auth-access__header h2 {
  color: var(--auth-ink);
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.2;
  margin: 0 0 0.1875rem;
}

.auth-access__header p {
  color: #7c8bb0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  line-height: 1.2;
  margin: 0;
  text-transform: uppercase;
}

.auth-access__copy {
  color: #8b98b8;
  font-size: 0.84375rem;
  font-weight: 600;
  line-height: 1.55;
  margin: 0 0 1.375rem;
}

.auth-footer {
  align-items: center;
  color: #5e6c90;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.6875rem;
  font-weight: 700;
  gap: 0.75rem 1.5rem;
  justify-content: space-between;
  letter-spacing: 0.08em;
  line-height: 1.4;
  margin: 0;
  max-width: none;
  padding: 0.625rem clamp(0.75rem, 1.5vw, 1.25rem);
  position: relative;
  text-transform: uppercase;
  width: 100%;
  z-index: 2;
}

.auth-footer span {
  align-items: center;
  display: inline-flex;
  gap: 0.4375rem;
}

.auth-footer .v-icon {
  color: #5e6c90;
  font-size: 0.875rem;
}

@keyframes auth-grid-drift {
  to {
    background-position:
      0 var(--grid-cell-size),
      var(--grid-cell-size) 0;
  }
}

@keyframes auth-online {
  0% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.5);
  }
  70% {
    box-shadow: 0 0 0 0.4375rem rgba(52, 211, 153, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
  }
}

@keyframes auth-radar-sweep {
  to {
    transform: rotate(360deg);
  }
}

@keyframes auth-radar-ring {
  0% {
    opacity: 0.55;
    transform: scale(0.6);
  }
  100% {
    opacity: 0;
    transform: scale(1.6);
  }
}

@keyframes auth-card-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.5625rem);
  }
}

@media (max-width: 58rem) {
  .auth-main {
    grid-template-columns: 1fr;
    justify-items: center;
    min-height: 0;
    padding: 1rem clamp(0.75rem, 2vw, 1.25rem) 1.25rem;
  }

  .auth-hero {
    align-items: center;
    max-width: 39rem;
    text-align: center;
  }

  .auth-hero__eyebrow {
    align-self: center;
  }

  .auth-hero__copy {
    margin-left: auto;
    margin-right: auto;
  }

  .auth-hero__signals {
    justify-content: center;
  }

  .auth-access {
    justify-self: center;
  }
}

@media (max-width: 40rem) {
  .auth-shell {
    padding: 0;
  }

  .auth-topbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.75rem;
  }

  .auth-brand {
    width: 100%;
  }

  .auth-brand__logo {
    height: 1.5rem;
    max-width: 11rem;
    width: 48vw;
  }

  .auth-main {
    gap: 1rem;
    padding: 0.25rem 0.75rem 0.875rem;
  }

  .auth-hero {
    align-items: flex-start;
    text-align: left;
    width: 100%;
  }

  .auth-hero__eyebrow {
    align-self: flex-start;
  }

  .auth-hero__signals {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .auth-access__radar {
    display: none;
  }

  .auth-footer {
    gap: 0.5rem;
    justify-content: flex-start;
    padding: 0.625rem 0.75rem 0.75rem;
    text-align: left;
  }
}

@media (max-width: 72rem) and (min-width: 58.0625rem) {
  .auth-main {
    gap: 1.25rem;
    grid-template-columns: minmax(0, 1fr) minmax(20rem, 27rem);
  }

  .auth-access {
    max-width: 27rem;
    width: 100%;
  }

  .auth-hero h1 {
    font-size: clamp(2.125rem, 4.5vw, 3rem);
  }
}

@media (max-width: 30rem) {
  .auth-brand {
    gap: 0.625rem;
  }

  .auth-brand__label {
    font-size: 0.625rem;
    letter-spacing: 0.12em;
  }

  .auth-status {
    font-size: 0.625rem;
    letter-spacing: 0.08em;
    padding: 0.375rem 0.625rem;
  }

  .auth-hero h1 {
    font-size: clamp(2rem, 12vw, 2.35rem);
  }

  .auth-hero h1 span {
    white-space: normal;
  }

  .auth-hero__copy {
    line-height: 1.55;
    margin: 0.875rem 0 1rem;
  }

  .auth-hero__signals span {
    font-size: 0.78125rem;
    padding: 0.5625rem 0.6875rem;
  }

  .auth-access__card {
    padding: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-page__grid,
  .auth-status__dot,
  .auth-access__radar::before,
  .auth-access__radar::after,
  .auth-access__card {
    animation: none;
  }
}
</style>
